import { Inject, Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { some } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { map, skip } from 'rxjs/operators';
import {
  FusionState,
  NestedOutputFilter,
} from '../../../../../../core/h3ml/state/fusion-state';
import { fusionStoreToken } from '../../../../../../core/h3ml/state/task-workspace-store';
import { FusionManager } from '../../../../../../core/outputs/annotation/annotation-shared/fusion/fusion-manager';
import { ShapeSelectionService } from '../../../../../../core/outputs/annotation/annotation-shared/utils/shape-selection.service';
import { contentConnectorToken } from '../../../../../../core/outputs/annotation/utils/misc-utils';
import { EventTrackingService } from '../../../../../../shared/event-tracking/event-tracking.service';
import { debounceOnMicroQueue } from '../../../../../../shared/misc-utilities/misc-utils';
import { ContentConnector } from '../../../../../../team-apis/content/content-connector';
import { AbstractAnnotationStore } from '../../../state/root/abstract-annotation-store';

@Injectable()
export class ShapeFilterService extends InjectableSuperclass {
  constructor(
    private store: AbstractAnnotationStore,
    private eventTrackingService: EventTrackingService,
    private selectionService: ShapeSelectionService,
    @Inject(contentConnectorToken)
    private contentConnector: ContentConnector,
    private fusionManager: FusionManager,
    @Inject(fusionStoreToken)
    private fusionStore: Store<FusionState>,
  ) {
    super();
    this.subscribeTo(
      this.fusionManager.fusionStore('nestedOutputFilter').$.pipe(
        // Skip initialization of the store.
        skip(1),
        // debouncing here so we only emit the latest value if multiple
        // observables are sent
        debounceOnMicroQueue(),
      ),
      this.updateSelectionAndContentStores.bind(this),
    );
  }

  showFilterEditor() {
    this.store.getActiveShapeLayerStore()!('transient').assign({
      shapeListHidden: true,
      shapeFiltersHidden: false,
    });
    this.fusionStore('isRightPanelExpanded').set(true);

    this.eventTrackingService.track({
      id: 'shape_list_filter',
      bucket: 'navigation',
      sub_bucket: 'frame',
      action: 'open_editor',
    });
  }

  closeFilterEditor() {
    this.store.getActiveShapeLayerStore()!('transient').assign({
      shapeListHidden: false,
      shapeFiltersHidden: true,
    });
    this.fusionStore('isRightPanelExpanded').set(true);

    this.eventTrackingService.track({
      id: 'shape_list_filter',
      bucket: 'navigation',
      sub_bucket: 'frame',
      action: 'close_editor',
    });
  }

  getNestedOutputShapeFilters$() {
    return this.fusionManager.fusionStore('nestedOutputFilter').$;
  }

  hasActiveFilters$() {
    return this.fusionManager.fusionStore('nestedOutputFilter').$.pipe(
      map((shapeFilters) =>
        some(shapeFilters, (shapeFilter) =>
          // @ts-ignore -- fix me
          some(shapeFilter.namesAndLabels, (f) => f.filterOut),
        ),
      ),
    );
  }

  hasFilters$() {
    return this.fusionManager.fusionStore('nestedOutputFilter').$.pipe(
      map((shapeFilters) =>
        some(
          shapeFilters,
          // @ts-ignore -- fix me
          (shapeFilter) => shapeFilter.namesAndLabels.length > 0,
        ),
      ),
    );
  }

  private updateSelectionAndContentStores(
    nestedOutputFilters: StrictRecord<string, NestedOutputFilter>,
  ) {
    if (isDefined(this.store.getActiveShapeLayerStore())) {
      this.selectionService.setSelection([]);
    }

    this.contentConnector.setShapeFilters(
      Object.keys(nestedOutputFilters).map((outputName) => ({
        outputName,
        // @ts-ignore -- fix me
        filterNames: nestedOutputFilters[outputName].namesAndLabels
          .filter((i) => i.filterOut)
          .map((i) => i.name),
      })),
    );
  }
}
